/**
 * @description 生成唯一 uuid
 * @returns {String}
 */
export function generateUUID() {
  let uuid = "";
  for (let i = 0; i < 32; i++) {
    let random = (Math.random() * 16) | 0;
    if (i === 8 || i === 12 || i === 16 || i === 20) uuid += "-";
    uuid += (i === 12 ? 4 : i === 16 ? (random & 3) | 8 : random).toString(16);
  }
  return uuid;
}

/**
 * 构造树型结构数据
 * @param {*} data 数据源
 * @param {*} id id字段 默认 'id'
 * @param {*} parentId 父节点字段 默认 'parentId'
 * @param {*} children 孩子节点字段 默认 'children'
 */
export function handleTree(data: any, id?: any, parentId?: any, children?: any) {
  let config = {
    id: id || "id",
    parentId: parentId || "parentId",
    childrenList: children || "children",
  };

  let childrenListMap: any = {};
  let nodeIds: any = {};
  let tree: any = [];

  for (let d of data) {
    let parentId = d[config.parentId];
    if (childrenListMap[parentId] == null) {
      childrenListMap[parentId] = [];
    }
    nodeIds[d[config.id]] = d;
    childrenListMap[parentId].push(d);
  }

  for (let d of data) {
    let parentId = d[config.parentId];
    if (nodeIds[parentId] == null) {
      tree.push(d);
    }
  }

  for (let t of tree) {
    adaptToChildrenList(t);
  }

  function adaptToChildrenList(o: any) {
    if (childrenListMap[o[config.id]] !== null) {
      o[config.childrenList] = childrenListMap[o[config.id]];
    }
    if (o[config.childrenList]) {
      for (let c of o[config.childrenList]) {
        adaptToChildrenList(c);
      }
    }
  }
  return tree;
}

/**
 * 把日期范围构造成 beginTime AND endTime
 * @param params 传递对象参数
 * @param dateRange 日期数组
 * @param propName1 自定义名称1
 * @param propName1 自定义名称2
 * @returns
 */
export function lcDatePicker(searchParams: object, dateRange: any, propName1?: string, propName2?: string) {
  searchParams = typeof searchParams === "object" && searchParams !== null && !Array.isArray(searchParams) ? searchParams : {};
  dateRange = Array.isArray(dateRange) ? dateRange : [];
  if (propName1 != null && propName1 != "" && propName2 != null && propName2 != "") {
    // 创建一个空的对象
    const dataParams: any = {};
    dataParams[propName1] = dateRange[0];
    dataParams[propName2] = dateRange[1];
    return Object.assign({}, searchParams, dataParams);
  } else {
    const dataParams = {
      beginTime: dateRange[0],
      endTime: dateRange[1],
    };
    return Object.assign({}, searchParams, dataParams);
  }
}

/**
 * 回显数据字典，进行状态翻译(数组对象)
 * @param datas 当前状态数据列表
 * @param value 需要进行翻译的值
 * @returns
 */
export function selectDictLabel(datas: any, value: any) {
  if (value === undefined || value === null || value === "") {
    return "";
  }
  let actions = [];
  Object.keys(datas).map((key) => {
    // 循环数据的获取key
    if (datas[key].dictValue === "" + value) {
      actions.push(datas[key].dictLabel);
      return true;
    }
  });
  if (actions.length === 0) {
    actions.push(value);
  }
  return actions.join("");
}

/**
 * @description 使用递归过滤出需要渲染在左侧菜单动态数据的列表 (需剔除 isHide == 0 隐藏的菜单)
 * @param {Array} menuList 菜单列表
 * @returns {Array}
 * */
export function getShowStaticAndDynamicMenuList(menuList: any) {
  let newMenuList: any = JSON.parse(JSON.stringify(menuList));
  return newMenuList.filter((item: any) => {
    return item.isHide == "1" || item.meta?.isHide == "1";
  });
}

/**
 * @description 使用递归找出所有面包屑存储到 pinia 中
 * @param {Array} menuList 菜单列表
 * @param {Array} parent 父级菜单
 * @param {Object} result 处理后的结果
 * @returns {Object}
 */
export const getAllBreadcrumbList = (menuList: any, parent = [], result: { [key: string]: any } = {}) => {
  for (const item of menuList) {
    result[item.path] = [...parent, item];

    if (item.children) getAllBreadcrumbList(item.children, result[item.path], result);
  }
  return result;
};

const mode = import.meta.env.VITE_ROUTER_MODE;
/**
 * @description 获取不同路由模式所对应的 url + params
 * @returns {String}
 */
export function getUrlWithParams() {
  const url = {
    hash: location.hash.substring(1),
    history: location.pathname + location.search,
  };
  if (typeof mode === "string" && mode in url) {
    return url[mode as keyof typeof url];
  }
  return null;
}

/**
 * @description 获取浏览器默认语言
 * @returns {String}
 */
export function getBrowserLang() {
  let browserLang = navigator.language || "";
  let defaultBrowserLang = "";
  if (["cn", "zh", "zh-cn"].includes(browserLang.toLowerCase())) {
    defaultBrowserLang = "zh";
  } else {
    defaultBrowserLang = "en";
  }
  return defaultBrowserLang;
}

/**
 * @description 格式化日期
 * @param url
 * @returns
 */
export function getTimeFormatted(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * 移除url参数
 * @param url
 * @returns
 */
export function removeQueryUsingURL(url: string): string {
  if (!url || url === "null" || typeof url === "undefined") {
    return "";
  }
  try {
    const urlObj = new URL(url);
    return urlObj.origin + urlObj.pathname;
  } catch (error) {
    return url.replace(/\?.+/, "");
  }
}

/**
 * 判断给定的文件名称是否为视频文件。
 * @param fileName - 文件的名称。
 * @returns 如果文件是视频，则返回 true；否则返回 false。
 */
export function isVideoFile(fileName: string): boolean {
  const videoExtensions = /\.(mp4|avi|mkv|mov|wmv|flv|webm|ogv)$/i;
  if (videoExtensions.test(fileName)) {
    return true;
  }
  const url = removeQueryUsingURL(fileName);
  return videoExtensions.test(url);
}

/**
 * 判断给定的文件名称是否为文档。
 * @param fileName - 文件的名称。
 * @returns 如果文件是文档，则返回 true；否则返回 false。
 */
export function isFile(fileName: string): boolean {
  const fileExtensions = /\.(doc|docx|pdf|txt|xls|xlsx)$/i;
  if (fileExtensions.test(fileName)) {
    return true;
  }
  const url = removeQueryUsingURL(fileName);
  return fileExtensions.test(url);
}

/**
 * 判断给定的文件名称是否为图片。
 * @param fileName - 文件的名称。
 * @returns 如果文件是图片，则返回 true；否则返回 false。
 */
export function isImageFile(fileName: string): boolean {
  const imageExtensions = /\.(jpg|jpeg|png|gif|bmp|webp)$/i;
  if (imageExtensions.test(fileName)) {
    return true;
  }
  const url = removeQueryUsingURL(fileName);
  return imageExtensions.test(url);
}

/**
 * 获取文件的后缀名（扩展名）。
 * @param fileName - 文件的名称。
 * @returns 文件的后缀名（扩展名），如果没有找到后缀名则返回空字符串。
 */
export function getFileExtension(fileName: string) {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex === -1) {
    return "";
  }
  return fileName.substring(lastDotIndex).toLowerCase();
}

/**
 * 读取文件的二进制数据。
 * @param file - 用户选择的文件对象。
 * @returns Promise，当文件读取完成后解析为 ArrayBuffer。
 */
export function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as ArrayBuffer);
    };
    reader.onerror = () => {
      reject(reader.error);
    };
    reader.readAsArrayBuffer(file);
  });
}

/**
 * 读取选择文件的 Base64 数据。
 * @param file - 用户选择的文件对象。
 * @returns Promise，当文件读取完成后解析为 Base64 字符串。
 */
export function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = () => {
      reject(reader.error);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * 移除字符串的所有空格
 * @param str
 * @returns
 */
export function removeSpaces(str: string): string {
  return `${str}`.replace(/\s+/g, "");
}

/**
 *  睡眠函数
 * @param ms
 * @returns
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * 获取预设日期范围
 * @param type
 * @returns
 */
export function getFormattedDateRange(type: string): [string, string] {
  const now = new Date();
  const startDate = new Date(now);
  const endDate = new Date(now);

  // 设置结束时间为当天的 23:59:59
  endDate.setHours(23, 59, 59, 999);
  switch (type) {
    case "today":
      startDate.setHours(0, 0, 0, 0);
      break;
    case "thisMonth":
      startDate.setDate(1); // 设置为本月的第一天
      startDate.setHours(0, 0, 0, 0);
      break;
    case "thisWeek":
      // 假设周一为每周的开始
      startDate.setDate(startDate.getDate() - startDate.getDay());
      startDate.setHours(0, 0, 0, 0);
      break;
    case "lastSevenDays":
      startDate.setDate(startDate.getDate() - 7 + 1); // 减去6天得到7天前的日期，并设置时间为00:00:00
      startDate.setHours(0, 0, 0, 0);
      break;
    case "lastThirtyDays":
      startDate.setDate(startDate.getDate() - 30 + 1); // 减去29天得到30天前的日期，并设置时间为00:00:00
      startDate.setHours(0, 0, 0, 0);
      break;
    default:
      throw new Error("Unsupported date range type");
  }
  return [getTimeFormatted(startDate), getTimeFormatted(endDate)];
}

/**
 * 通过区划代码获取短区划
 * @param str
 * @returns
 */
export const getShortAreaCode = (areaCode: string): string => {
  if (!areaCode) return "";

  let str = `${areaCode}`;
  if (str === "1") return "1";

  str = str.replace(/[0]+$/g, "");
  const len = str.length;
  if (len > 6) {
    if (len === 7 || len === 10) {
      str = `${str}00`;
    } else if (len === 8 || len === 11) {
      str = `${str}0`;
    }
  } else {
    if (len % 2 === 1) {
      str = `${str}0`;
    }
  }
  return str;
};

/**
 * 获取每厘米对应的像素数
 * @returns
 */
export const getPixelsPerCentimeter = () => {
  const div = document.createElement("div");
  div.style.width = "1cm";
  div.style.position = "absolute";
  div.style.visibility = "hidden";
  document.body.appendChild(div);
  const pixelsPerCm = div.offsetWidth;
  document.body.removeChild(div);
  return pixelsPerCm;
};

/**
 * 保留小数位舍去后面部分
 * @param num
 * @param decimals
 * @returns
 */
export const truncateDecimals = (num: number, decimals = 2) => {
  const factor = Math.pow(10, decimals);
  return Math.trunc(num * factor) / factor;
};

/**
 * 格式化树形字典
 * @param list
 * @returns
 */
export const formatDicTree = (list: Array<any>) => {
  if (!list) {
    return null;
  }
  return list.map((item) => {
    item.code = item.object?.code;
    item.name = item.object?.name;
    if (item.children) {
      formatDicTree(item.children);
    }
    return item;
  });
};

// 判断是否为空值（空数组、空对象、空字符串、null）
const isEmpty = (value: unknown) => {
  if (value === null || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0) return true;
  return false;
};

/**
 * 判断对象属性值是否相同
 * @param x
 * @param y
 * @returns
 */
export const deepObjectEqual = (x: any, y: any): boolean => {
  if (isEmpty(x) && isEmpty(y)) {
    return true;
  }

  if (isEmpty(x) || isEmpty(y)) {
    return false;
  }

  if (Array.isArray(x) && Array.isArray(y)) {
    if (x.length !== y.length) {
      return false;
    }
    return x.every((item, index) => deepObjectEqual(item, y[index]));
  }

  if (typeof x === "object" && typeof y === "object") {
    const aKeys = Object.keys(x);
    const bKeys = Object.keys(y);
    if (aKeys.length !== bKeys.length) {
      return false;
    }
    // eslint-disable-next-line no-prototype-builtins
    return aKeys.every((key) => y.hasOwnProperty(key) && deepObjectEqual(x[key], y[key]));
  }

  return x === y;
};

/**
 * 检查字符串中是否有大写字母
 * @param str
 * @returns
 */
export const containsUpper = (str: string): boolean => {
  return /[A-Z]/.test(str);
};

/**
 * 格式化云查询元数据信息
 */
export const formatCloudQueryMetaData = (list: any[]): any => {
  const data: any = {};
  list.forEach((item: any) => {
    const keys = Object.keys(item);
    keys.forEach((key) => {
      if (containsUpper(key)) {
        const k = key.toLowerCase();
        item[k] = item[key];
      }
    });
    if (!data[item.tag]) {
      data[item.tag] = [item];
    } else {
      data[item.tag].push(item);
    }
  });
  Object.keys(data).forEach((key) => {
    data[key] = data[key].sort((a: any, b: any) => {
      if (!a.time && !b.time) return 0;
      if (!a.time) return 1;
      if (!b.time) return -1;
      return b.time - a.time;
    });
  });
  return data;
};

/**
 * 将树转为一维数组
 * @param tree
 * @param childrenKey
 * @returns
 */
export const flattenTree = (tree: any[], childrenKey = "children") => {
  const result: any[] = [];

  function traverse(node: { [k: string]: any }) {
    const { [childrenKey]: children, ...rest } = node;
    result.push(rest);

    if (children && Array.isArray(children)) {
      children.forEach((child) => traverse(child));
    }
  }

  tree.forEach((node) => traverse(node));
  return result;
};
